import axios, { AxiosError } from 'axios'
import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios'
import router from '@/router'

// Create axios instance with default config
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  },
  withCredentials: true
})

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from localStorage
    const token = localStorage.getItem('auth_token')
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response) {
      const status = error.response.status

      switch (status) {
        case 401: {
          // Skip for logout request itself to avoid infinite loop
          const reqUrl = error.config?.url || ''
          if (!reqUrl.includes('/logout')) {
            localStorage.removeItem('auth_token')
            delete api.defaults.headers.common['Authorization']
            window.dispatchEvent(new CustomEvent('auth:unauthenticated', {
              detail: { redirect: router.currentRoute.value.fullPath }
            }))
          }
          break
        }
          
        case 403:
          // Forbidden
          console.error('Access forbidden')
          break
          
        case 404:
          // Not found
          console.error('Resource not found')
          break
          
        case 422:
          // Validation error - let the component handle it
          break
          
        case 500:
          // Server error
          console.error('Server error occurred')
          break
      }
    } else if (error.request) {
      // No response was received. In axios this is also what a CORS-blocked
      // request looks like (the browser rejects it before we get a response).
      // The usual real-world cause is NOT a CORS misconfig but an API host that
      // is down / unreachable (e.g. a dead ngrok tunnel, proxy or CDN error
      // page) - those return HTML with no CORS headers and surface here.
      const target = error.config?.baseURL || import.meta.env.VITE_API_URL
      console.error(
        `[api] No response from API host "${target}" — ` +
        'check that the backend/tunnel is up. Browser will also log a CORS error. ' +
        `(${error.code || 'network error'})`
      )
      // Attach a user-facing message + flag so callers can surface it instead
      // of failing silently. (unreachableMessage is hoisted, defined below.)
      error.message = unreachableMessage()
      ;(error as AxiosError & { isUnreachable?: boolean }).isUnreachable = true
    }
    
    return Promise.reject(error)
  }
)

export default api

// True when axios got NO response at all. This is what a CORS-blocked request
// looks like, and - critically - also a request whose host is simply
// unreachable (dead tunnel, WAF/rate-limit page, network/VPN/ad-blocker block).
export function isUnreachableError(error: unknown): boolean {
  const e = error as AxiosError & { isUnreachable?: boolean }
  return !!(e && (e.isUnreachable || (!e.response && e.request)))
}

// A clear, user-facing explanation for the case above, so the ~1% of users
// whose browser can't complete the request see what's wrong instead of a
// misleading generic / "CORS" failure.
export function unreachableMessage(): string {
  let host = ''
  try {
    host = new URL(import.meta.env.VITE_API_URL || window.location.origin).host
  } catch {
    host = 'the API server'
  }
  return (
    `Unable to connect to the server (${host}). Your browser blocked or could ` +
    'not complete the request. This is usually caused by a VPN, an ad/tracker ' +
    'blocker, a browser extension, antivirus web-shield, or a restricted ' +
    'network. Please try disabling those / switching networks, then retry.'
  )
}

// Helper functions for common HTTP methods
export const apiGet = <T>(url: string, params?: object) => 
  api.get<T>(url, { params })

export const apiPost = <T>(url: string, data?: object) => 
  api.post<T>(url, data)

export const apiPut = <T>(url: string, data?: object) => 
  api.put<T>(url, data)

export const apiDelete = <T>(url: string) => 
  api.delete<T>(url)

export const apiPatch = <T>(url: string, data?: object) => 
  api.patch<T>(url, data)
